import { Card, CardContent } from "@/components/ui/card";
import { RhythmData } from "@/types/dashboard";
import { Mask } from "@/utils/mask";
import { Activity, TrendingUp, TrendingDown, Target } from "lucide-react";

interface RhythmCardProps {
  data: RhythmData;
  goalType?: "meta" | "super_meta";
}

export function RhythmCard({ data, goalType = "meta" }: RhythmCardProps) {
  const difference = isNaN(data.difference) ? 0 : data.difference;
  const currentDailyAverage = isNaN(data.current_daily_average) ? 0 : data.current_daily_average;
  const requiredDailyAverage = isNaN(data.required_daily_average) ? 0 : data.required_daily_average;
  const monthProjection = isNaN(data.month_projection) ? 0 : data.month_projection;
  const isAbove = difference >= 0;
  const daysPassed = isNaN(data.days_passed) ? 0 : Math.round(data.days_passed);
  const daysRemaining = isNaN(data.days_remaining) ? 0 : Math.round(data.days_remaining);
  const goalLabel = goalType === "super_meta" ? "super meta" : "meta";

  return (
    <Card className="h-full border-0 shadow-lg bg-gradient-to-br from-violet-600 via-violet-700 to-purple-800 text-white">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-white/20 backdrop-blur rounded-lg">
              <Activity className="h-4 w-4" />
            </div>
            <span className="font-medium">Ritmo de Vendas</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-white/10 backdrop-blur rounded-lg p-2">
            <p className="text-violet-200 text-xs mb-1">Média/dia</p>
            <p className="text-sm font-bold truncate">{Mask.formatCurrency(currentDailyAverage)}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-2">
            <p className="text-violet-200 text-xs mb-1">Necessário/dia</p>
            <p className="text-sm font-bold truncate">{Mask.formatCurrency(requiredDailyAverage)}</p>
          </div>
        </div>

        {/* Status */}
        <div className={`flex items-center gap-2 p-2 rounded-xl mb-4 ${
          isAbove ? "bg-green-500/20" : "bg-red-500/20"
        }`}>
          {isAbove ? (
            <TrendingUp className="h-4 w-4 flex-shrink-0" />
          ) : (
            <TrendingDown className="h-4 w-4 flex-shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-xs font-medium">
              {isAbove ? `Acima da ${goalLabel}` : `Abaixo da ${goalLabel}`}
            </p>
            <p className="text-sm font-bold truncate">
              {isAbove ? "+" : ""}{Mask.formatCurrency(difference)}/dia
            </p>
          </div>
        </div>

        {/* Projection */}
        <div className="pt-2 border-t border-white/20">
          <div className="flex items-center gap-1 mb-1">
            <Target className="h-3 w-3 text-violet-200 flex-shrink-0" />
            <span className="text-xs text-violet-100 truncate">Projeção: <span className="font-bold text-white">{Mask.formatCurrency(monthProjection)}</span></span>
          </div>
          <p className="text-xs text-violet-200">
            {daysPassed} dias passados • {daysRemaining} restantes
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
